import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useLogout } from '@privy-io/react-auth'
import { Button, Icon, Card, CardRow, CardDivider, Header, Modal } from '../components/ui'
import { useStore } from '../store'
import { getTransaction, type TransactionResponse } from '../services/payment'
import { logScreenView, logPaymentCompleted } from '../services/analytics'

export function SuccessScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { resetReceipt, resetPayment, resetAll } = useStore()
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const { logout } = useLogout()

  // Get transaction_id from URL (set by API in success redirect)
  const transactionId = searchParams.get('transaction_id')

  const [transaction, setTransaction] = useState<TransactionResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch transaction details on mount
  useEffect(() => {
    logScreenView('success')

    async function fetchTransaction() {
      if (!transactionId) {
        setLoading(false)
        return
      }

      try {
        const data = await getTransaction(transactionId)
        setTransaction(data)
        logPaymentCompleted(transactionId, data.totalCents / 100, data.company?.name || '')
      } catch (err) {
        console.error('Failed to fetch transaction:', err)
        setError(err instanceof Error ? err.message : 'Failed to load transaction details')
      } finally {
        setLoading(false)
      }
    }

    fetchTransaction()
   
  }, [transactionId])

  const handleDone = () => {
    // Reset receipt and payment state for next transaction
    resetReceipt()
    resetPayment()
    // Navigate to capture for next payment (user stays logged in)
    navigate('/capture')
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col">
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin inline-block w-8 h-8 border-4 border-[var(--color-primary)] border-t-transparent rounded-full mb-4" />
            <p className="text-[var(--color-text-muted)]">Loading payment details...</p>
          </div>
        </main>
      </div>
    )
  }

  // Show success even without payment details (fallback)
  const amount = transaction ? transaction.totalCents / 100 : 0
  const venueName = transaction?.company?.name || 'Unknown Venue'
  const displayTransactionId = transactionId || 'Unknown'
  const timestamp = transaction?.createdAt ? new Date(transaction.createdAt) : new Date()

  return (
    <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col">
      <Header
        showLogo
        rightAction={
          <button
            onClick={() => setShowLogoutModal(true)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors"
          >
            <Icon name="logout" size={22} className="text-[var(--color-text-muted)]" />
          </button>
        }
      />

      <Modal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title="Sign out?"
        confirmLabel="Sign out"
        onConfirm={() => {
          // Clear state FIRST, then logout from Privy
          resetAll()
          logout()
          navigate('/')
        }}
        variant="danger"
        icon="logout"
      >
        Are you sure you want to sign out of your account?
      </Modal>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Success Animation */}
        <div className="relative mb-8">
          {/* Outer ring - single pulse */}
          <div className="absolute inset-0 w-32 h-32 rounded-full bg-[var(--color-success)]/20 animate-[pulse-once_0.6s_ease-out]" />

          {/* Success circle */}
          <div className="relative w-32 h-32 rounded-full bg-[var(--color-success)] flex items-center justify-center shadow-lg animate-[scale-in_0.4s_ease-out]">
            <Icon name="check" size={64} className="text-white" strokeWidth={3} />
          </div>
        </div>

        {/* Success Message */}
        <h1 className="text-3xl font-semibold text-[var(--color-text)] mb-2 text-center">
          Payment Complete!
        </h1>
        <p className="text-[var(--color-text-muted)] text-center mb-8">
          Thank you for using Finch Pay
        </p>

        {/* Error message if fetch failed but we still show success */}
        {error && (
          <p className="text-sm text-amber-600 mb-4 text-center">
            Could not load full details, but your payment was successful.
          </p>
        )}

        {/* Payment Details Card */}
        <Card className="w-full max-w-sm mb-8">
          <div className="text-center mb-6">
            <p className="text-sm text-[var(--color-text-muted)] mb-1">Amount Paid</p>
            <p className="text-4xl font-semibold text-[var(--color-text)]">
              ${amount.toFixed(2)}
            </p>
          </div>

          <CardDivider />

          <div className="space-y-4">
            <CardRow label="Venue" value={venueName} />
            <CardRow
              label="Transaction ID"
              value={displayTransactionId.substring(0, 8) + '...'}
              mono
            />
            <CardRow
              label="Date"
              value={timestamp.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            />
            {transaction?.status && (
              <CardRow
                label="Status"
                value={transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
              />
            )}
          </div>
        </Card>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Done Button */}
        <div className="w-full max-w-sm">
          <Button onClick={handleDone}>
            Done
          </Button>
        </div>
      </main>
    </div>
  )
}
